import { jsx as _jsx, jsxs as _jsxs, Fragment as _Fragment } from "react/jsx-runtime";
import * as React from "react";
import { Slot } from "@radix-ui/react-slot";
import { cva } from "class-variance-authority";
import { Loader } from "lucide-react";
import { cn } from "../../../utils";
const buttonVariants = cva("inline-flex items-center justify-center gap-2 rounded-xl font-semibold ease-in duration-100 border focus:outline-hidden focus:ring-2 focus:ring-offset-2 focus:ring-info-solid disabled:cursor-not-allowed disabled:opacity-60 disabled:pointer-events-none aria-disabled:cursor-not-allowed aria-disabled:opacity-60 [&_svg]:pointer-events-none [&_svg]:size-5 [&_svg]:shrink-0", {
    variants: {
        variant: {
            primary: "bg-black-2 border-black-2 text-white hover:bg-black/90 active:bg-black",
            "primary-light": "border-normal hover:bg-gray-primary active:bg-black-2",
            secondary: "border-blue-primary text-blue-primary hover:bg-blue-light-5 active:bg-blue-light-3 active:text-black",
            tertiary: "border-transparent shadow shadow-[#A6AFC366] text-blue-primary hover:bg-gray-2 active:bg-blue-light-5",
            destructive: "bg-destructive text-destructive-foreground hover:bg-destructive/90",
            outline: "border border-input bg-background hover:bg-accent hover:text-accent-foreground",
            ghost: "border-transparent hover:bg-accent hover:text-accent-foreground",
            brand: "bg-bliks-green-500 border-bliks-green-500 text-foreground hover:bg-bliks-green-400 hover:border-bliks-green-400 active:bg-bliks-green-600 active:border-bliks-green-600",
            link: "border-transparent text-primary underline-offset-4 hover:underline"
        },
        size: {
            small: "h-11 px-3 text-xs",
            medium: "h-11 px-4 text-sm",
            large: "h-16 px-6 text-base",
            icon: "h-10 w-10",
            "icon-sm": "h-8 w-8 [&_svg]:size-4"
        },
        // Sentence-long labels (German copy runs long) would otherwise extend
        // past their container and get clipped: the label wraps and the button
        // grows downward instead. What that means depends on the size, so the
        // classes live in the compound variants below — a fixed square has no
        // room to grow, and no label to wrap.
        wrap: {
            true: "",
            false: "whitespace-nowrap"
        }
    },
    // cva always emits compound classes last, so the height swap can't be
    // undone by reordering the variants above (tailwind-merge is last-wins).
    // Each combination emits exactly one white-space class, so the output is
    // also correct for a caller that skips `cn`.
    compoundVariants: [
        {
            size: [
                "small",
                "medium"
            ],
            wrap: true,
            class: "h-auto min-h-11 py-2 text-left whitespace-normal"
        },
        {
            size: "large",
            wrap: true,
            class: "h-auto min-h-16 py-3 text-left whitespace-normal"
        },
        {
            size: [
                "icon",
                "icon-sm"
            ],
            wrap: true,
            class: "whitespace-nowrap"
        }
    ],
    defaultVariants: {
        variant: "primary",
        size: "medium",
        wrap: false
    }
});
const Button = /*#__PURE__*/ React.forwardRef(({ className, variant, size, wrap, asChild = false, loading = false, disabled, children, ...props }, ref)=>{
    const Comp = asChild ? Slot : "button";
    return /*#__PURE__*/ _jsx(Comp, {
        className: cn(buttonVariants({
            variant,
            size,
            wrap
        }), loading && "relative", className),
        ref: ref,
        disabled: loading || disabled,
        "aria-busy": loading || undefined,
        ...props,
        children: loading ? /*#__PURE__*/ _jsxs(_Fragment, {
            children: [
                /*#__PURE__*/ _jsx("span", {
                    className: "inline-flex items-center justify-center gap-2 opacity-0",
                    children: children
                }),
                /*#__PURE__*/ _jsx("span", {
                    "aria-hidden": true,
                    className: "absolute inset-0 flex items-center justify-center",
                    children: /*#__PURE__*/ _jsx(Loader, {
                        className: "animate-spin"
                    })
                })
            ]
        }) : children
    });
});
Button.displayName = "Button";
export { Button, buttonVariants };

//# sourceMappingURL=button.js.map